import React, { useState, useEffect } from 'react';
import { MinusOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, InputField, InputWrapper } from './PlusMinusInput.styles';

interface PlusMinusInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  width?: number | string;
  disabled?: boolean;
}

const PlusMinusInput: React.FC<PlusMinusInputProps> = ({
  value,
  onChange,
  min = 0,
  max = Infinity,
  step = 1,
  width,
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState(value.toString());

  useEffect(() => {
    setInputValue(value.toString());
  }, [value]);

  const handleIncrement = () => {
    if (disabled || value >= max) return;
    onChange(Math.min(value + step, max));
  };

  const handleDecrement = () => {
    if (disabled || value <= min) return;
    onChange(Math.max(value - step, min));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isNumber = /^[0-9]*$/.test(e.target.value);

    if (!isNumber) return;

    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    let newValue = parseFloat(inputValue);
    if (Number.isNaN(newValue)) {
      newValue = value;
    } else {
      newValue = Math.max(min, Math.min(max, newValue));
    }
    onChange(newValue);
    setInputValue(newValue.toString());
  };

  return (
    <InputWrapper $width={width}>
      <Button
        $disabled={disabled || value <= min}
        disabled={disabled || value <= min}
        onClick={handleDecrement}
        type="button"
        aria-label="Decrease value"
        data-cy="decreaseOrder_btn"
      >
        <MinusOutlined size={18} />
      </Button>
      <InputField
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            handleInputBlur();
          }
        }}
        disabled={disabled}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
      />
      <Button
        $disabled={disabled || value >= max}
        disabled={disabled || value >= max}
        onClick={handleIncrement}
        type="button"
        aria-label="Increase value"
        data-cy="increaseOrder_btn"
      >
        <PlusOutlined size={18} />
      </Button>
    </InputWrapper>
  );
};

export default PlusMinusInput;
